import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import api from '../services/api';

const appUrl = 'https://bettermedaily.co.za';
const blogUrl = 'https://bettermedaily.co.za/index.html';
const privacyUrl = 'https://bettermedaily.co.za/privacy.html';
const mainTermsUrl = 'https://bettermedaily.co.za/terms.html';
const supportEmail = 'affiliates@bettermedaily.co.za';
const logoUrl = '/betterme_logo.png';

const linkClass = ({ isActive }) =>
  `block sm:inline transition-colors ${
    isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-900'
  }`;

export const AffiliateHeader = ({ showDashboardLink = true }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(api.isAuthenticated());

  useEffect(() => api.subscribeAuth(setIsLoggedIn), []);

  const handleLogout = () => {
    api.logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-b border-gray-100 z-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group" onClick={() => setMenuOpen(false)}>
            <img
              src={logoUrl}
              alt="Better Me logo"
              className="w-10 h-10 object-contain group-hover:scale-105 transition-transform"
            />
            <span className="font-extrabold text-base sm:text-lg tracking-[-0.04em] text-slate-900">Better Me</span>
          </Link>

          <div className="hidden sm:flex items-center gap-7 text-sm font-medium">
            <NavLink to="/" className={linkClass}>Home</NavLink>
            <NavLink to="/about" className={linkClass}>About</NavLink>
            <Link to="/#affiliate-posts" className="text-gray-600 hover:text-gray-900 transition-colors">Blog</Link>
            {isLoggedIn ? (
              <>
                {showDashboardLink && <NavLink to="/dashboard" className={linkClass}>Dashboard</NavLink>}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <NavLink to="/login" className={linkClass}>Sign in</NavLink>
            )}
            <a
              href={appUrl}
              className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white text-sm px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Get App
            </a>
          </div>

          <div className="sm:hidden flex items-center gap-2">
            {!isLoggedIn && (
              <NavLink
                to="/login"
                className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 hover:border-gray-300"
                onClick={() => setMenuOpen(false)}
              >
                Sign in
              </NavLink>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((current) => !current)}
              className="p-2 text-gray-600"
              aria-label="Toggle mobile menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="sm:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-4 space-y-3 text-sm font-medium">
            <NavLink to="/" className={linkClass} onClick={() => setMenuOpen(false)}>Home</NavLink>
            <NavLink to="/about" className={linkClass} onClick={() => setMenuOpen(false)}>About</NavLink>
            <Link to="/#affiliate-posts" className="block text-gray-600 hover:text-gray-900" onClick={() => setMenuOpen(false)}>
              Blog
            </Link>
            {isLoggedIn && (
              <>
                {showDashboardLink && <NavLink to="/dashboard" className={linkClass} onClick={() => setMenuOpen(false)}>Dashboard</NavLink>}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="block text-left text-gray-600 hover:text-gray-900"
                >
                  Logout
                </button>
              </>
            )}
            <a
              href={appUrl}
              className="block bg-primary-600 hover:bg-primary-700 text-white text-center px-4 py-3 rounded-lg font-semibold transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              Get App
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export const AffiliateFooter = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(api.isAuthenticated());

  useEffect(() => api.subscribeAuth(setIsLoggedIn), []);

  return (
  <footer className="bg-gray-900 text-white py-12 px-4">
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={logoUrl} alt="Better Me logo" className="w-10 h-10 object-contain" />
            <span className="font-bold text-xl">Better Me</span>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            Your AI companion for journaling, habits, spending awareness, and everyday self-reflection.
          </p>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Product</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/" className="hover:text-white transition-colors">Affiliate Program</Link></li>
            <li><Link to="/about" className="hover:text-white transition-colors">About Better Me</Link></li>
            <li>
              <Link to={isLoggedIn ? '/dashboard' : '/login'} className="hover:text-white transition-colors">
                {isLoggedIn ? 'Dashboard' : 'Sign in'}
              </Link>
            </li>
            <li><a href={blogUrl} className="hover:text-white transition-colors">Features</a></li>
            <li><Link to="/#affiliate-posts" className="hover:text-white transition-colors">Blog</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Resources</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/terms" className="hover:text-white transition-colors">Affiliate Terms</Link></li>
            <li><Link to="/payout" className="hover:text-white transition-colors">Request Payout</Link></li>
            <li><a href={mainTermsUrl} className="hover:text-white transition-colors">Main App Terms</a></li>
            <li><a href={privacyUrl} className="hover:text-white transition-colors">Privacy Policy</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Support & Legal</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><a href={`mailto:${supportEmail}`} className="hover:text-white transition-colors">Contact Support</a></li>
            <li><a href={privacyUrl} className="hover:text-white transition-colors">Privacy Policy</a></li>
            <li><a href={mainTermsUrl} className="hover:text-white transition-colors">Terms of Service</a></li>
            <li><a href={`mailto:${supportEmail}`} className="hover:text-white transition-colors">{supportEmail}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col sm:flex-row justify-between gap-3 text-sm text-gray-400">
        <p>&copy; 2026 Better Me. All rights reserved.</p>
        <p>Built in South Africa. Not a crisis, clinical, legal, or financial-advice service.</p>
      </div>
    </div>
  </footer>
  );
};

const SiteChrome = ({ children, showDashboardLink = true }) => (
  <div className="min-h-screen bg-gray-50 text-gray-900 antialiased">
    <AffiliateHeader showDashboardLink={showDashboardLink} />
    <main className="pt-16">{children}</main>
    <AffiliateFooter />
  </div>
);

export default SiteChrome;
